import React, { useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { feedPostRequest, FeedPostRequestError } from '../../lib/feedPostApi';
import {
  PARTNER_DAYS,
  PARTNER_NOTE_MAX,
  PARTNER_TIMES,
  type PartnerDay,
  type PartnerTime,
} from '../../shared/utils/feed-partner';

type Props = {
  onCancel: () => void;
  onPosted: () => void;
};

/**
 * B2 (akış araştırması §13): "Partner arıyorum" hızlı gönderisi. Gün ve saat seçilir, isteğe bağlı kısa
 * not; sunucu kesin tarihi ekler ve süresi geçince kart kapanır. Maç şartı yok (yeni üye partner bulsun),
 * günlük paylaşım sınırı aynı. Başarı sunucu onayından sonra.
 */
export default function FeedPartnerForm({ onCancel, onPosted }: Props) {
  const { t } = useTranslation('leagues');
  const [gun, setGun] = useState<PartnerDay | null>(null);
  const [zaman, setZaman] = useState<PartnerTime | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!gun || !zaman || busy) return;
    setBusy(true);
    setError('');
    try {
      await feedPostRequest('POST', {}, { partner: { gun, zaman }, text: note });
      onPosted();
    } catch (failure) {
      const { code } = failure as FeedPostRequestError;
      if (code === 'BLOCKED') setError(t('detail.posts.blocked'));
      else if (code?.startsWith('NOT_ALLOWED_'))
        setError(t(`detail.posts.reason.${code.slice('NOT_ALLOWED_'.length)}`));
      else setError(t('detail.posts.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const chips = <T extends string>(
    name: string,
    items: readonly T[],
    value: T | null,
    // eslint-disable-next-line no-unused-vars -- callback type signature
    set: (item: T) => void,
  ) => (
    <div className="feed-partner__chips" role="group" aria-labelledby={`feed-partner-${name}`}>
      {items.map(item => (
        <Button
          key={item}
          className={`feed-partner__chip${value === item ? ' is-active' : ''}`}
          aria-pressed={value === item}
          onClick={() => set(item)}
          disabled={busy}
        >
          {t(`detail.posts.partner.${name}.${item}`)}
        </Button>
      ))}
    </div>
  );

  return (
    <form
      className="feed-composer feed-composer--open feed-partner"
      aria-busy={busy}
      aria-labelledby="feed-partner-title"
      onSubmit={event => {
        event.preventDefault();
        submit();
      }}
    >
      <h3 id="feed-partner-title" className="feed-partner__title">
        <span aria-hidden="true">🎾</span> {t('detail.posts.partner.title')}
      </h3>
      <p id="feed-partner-gun" className="feed-composer__label">
        {t('detail.posts.partner.dayLabel')}
      </p>
      {chips('gun', PARTNER_DAYS, gun, setGun)}
      <p id="feed-partner-zaman" className="feed-composer__label">
        {t('detail.posts.partner.timeLabel')}
      </p>
      {chips('zaman', PARTNER_TIMES, zaman, setZaman)}
      <label htmlFor="feed-partner-note" className="feed-composer__label">
        {t('detail.posts.partner.noteLabel')}
      </label>
      <Input
        id="feed-partner-note"
        value={note}
        onChange={event => setNote(event.target.value)}
        maxLength={PARTNER_NOTE_MAX}
        showCount
        placeholder={t('detail.posts.partner.notePlaceholder')}
        disabled={busy}
      />
      {error && <Alert role="alert" showIcon type="error" message={error} />}
      <div className="feed-composer__actions">
        <span className="feed-composer__spacer" />
        <Button type="text" onClick={onCancel} disabled={busy}>
          {t('detail.posts.cancel')}
        </Button>
        <Button
          htmlType="submit"
          className="feed-composer__submit"
          loading={busy}
          disabled={!gun || !zaman}
          title={!gun || !zaman ? t('detail.posts.partner.pick') : undefined}
        >
          {t('detail.posts.submit')}
        </Button>
      </div>
    </form>
  );
}
